import SEO from "../components/SEO";
import ImpersonationBanner from "../components/ImpersonationBanner";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatNZDate, formatNZDateTime, getNZNow } from "../utils/timezone";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import DatePicker from "../components/DatePicker";
import ReceiptDialog from "../components/ReceiptDialog";
import { format, parse, isValid } from "date-fns";
import { cn } from "../lib/utils";
import { 
  Sun, Moon, LogOut, Clock, Zap, MapPin, Star, 
  ChevronRight, DollarSign, Calendar as CalendarIcon, Store, FileText,
  CheckCircle, AlertCircle, Package, Eye, EyeOff, Send, TrendingUp, Edit, Lock, Receipt, User, Trash2,
  Building2, Phone, Mail, Pencil, MessageSquare, Settings, X
} from "lucide-react";
import { ShopQuoteFormDialog, blankQuoteSlot } from "./shop/ShopQuoteFormDialog";
import { ShopQuoteViewDialog } from "./shop/ShopQuoteViewDialog";
import { ShopOrderDialogs } from "./shop/ShopOrderDialogs";
import { ShopMyQuotesGroupedList } from "./shop/ShopMyQuotesGroupedList";
import FranchiseReturnLink from "../components/FranchiseReturnLink";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

const SERVICE_TYPE_LABELS = {
  tyres_only: "Tyres Only",
  tyres_alignment: "Tyres + Alignment",
  tyres_rotation: "Tyres + Rotation",
  tyres_alignment_rotation: "Tyres + Alignment + Rotation",
  alignment_only: "Alignment Only",
  alignment_rotation: "Alignment + Rotation",
  rotation_only: "Rotation Only"
};

// Safely format a date value (string "dd/MM/yyyy" or Date object) to "yyyy-MM-dd"
const formatDateForApi = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === "string") {
    const parsed = parse(dateVal, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : dateVal;
  }
  return format(dateVal, "yyyy-MM-dd");
};


// Helper function to get service label from selected_services array
const getServiceLabel = (request) => {
  // Build label from selected_services for accuracy (priority over service_type)
  const services = request.selected_services || [];
  if (services.length > 0) {
    const parts = [];
    if (services.includes("tyres")) parts.push("Tyres");
    if (services.includes("alignment")) parts.push("Alignment");
    if (services.includes("rotation")) parts.push("Rotation");
    if (parts.length > 0) return parts.join(" + ");
  }
  
  // Fall back to service_type map
  if (request.service_type && SERVICE_TYPE_LABELS[request.service_type]) {
    return SERVICE_TYPE_LABELS[request.service_type];
  }
  
  return "Service";
};

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-600 border-green-500/30",
  completed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  expired: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  paid: "bg-green-500/10 text-green-600 border-green-500/30",
  confirmed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  submitted: "bg-blue-500/10 text-blue-600 border-blue-500/30"
};

export default function ShopDashboard() {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [availableRequests, setAvailableRequests] = useState([]);
  const [hiddenRequestIds, setHiddenRequestIds] = useState([]);
  const [requestTab, setRequestTab] = useState("active");
  const [pendingApproval, setPendingApproval] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [shopSuspended, setShopSuspended] = useState(false);
  const [suspensionInfo, setSuspensionInfo] = useState(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [submittingPause, setSubmittingPause] = useState(false);
  const [myQuotes, setMyQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [activeTab, setActiveTab] = useState("available");
  const [viewQuoteDialogOpen, setViewQuoteDialogOpen] = useState(false);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [editQuoteMode, setEditQuoteMode] = useState(false);
  const [savingQuoteEdit, setSavingQuoteEdit] = useState(false);
  const [confirmAvailabilityDialogOpen, setConfirmAvailabilityDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [availabilityForm, setAvailabilityForm] = useState({
    stock_confirmed: true,
    proposed_service_date: null,
    proposed_service_time: "",
    notes: ""
  });
  const [submittingAvailability, setSubmittingAvailability] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [shopCommission, setShopCommission] = useState(0);
  
  // New workflow state
  const [confirmJobDialogOpen, setConfirmJobDialogOpen] = useState(false);
  const [assignDateDialogOpen, setAssignDateDialogOpen] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [selectedOrderForAction, setSelectedOrderForAction] = useState(null);
  const [confirmingJob, setConfirmingJob] = useState(false);
  const [assigningDate, setAssigningDate] = useState(false);
  const [completionForm, setCompletionForm] = useState({
    service_date: "",
    service_time: "",
    payment_method: "",
    completion_notes: ""
  });
  const [serviceDateForm, setServiceDateForm] = useState({
    service_date: "",
    service_time: ""
  });

  // Account/Profile state
  const [shopProfile, setShopProfile] = useState(null);
  const [shopReviews, setShopReviews] = useState({ reviews: [], stats: {} });
  const [profileForm, setProfileForm] = useState({
    shop_name: "",
    phone: "",
    service_radius_km: 50,
    address: {}
  });

  // Quote form state - simplified pricing with GST toggle
  const [quoteForm, setQuoteForm] = useState({
    tyre_brand: "",
    tyre_model: "",
    speed_rating: "",
    tyre_fitment_cost: "",   // Tyre & Fitment cost
    alignment_cost: "",       // Wheel Alignment cost
    rotation_cost: "",        // Tyre Rotation cost (separate from tyre fitment)
    prices_include_gst: false, // Toggle: true = prices include GST, false = prices exclude GST
    service_date_estimate: null,
    quote_expiry: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    notes: "",
    // Editable tyre specs
    edit_width: "",
    edit_profile: "",
    edit_rim_size: ""
  });
  const [confirmQuoteDialogOpen, setConfirmQuoteDialogOpen] = useState(false);

  // ---- Multi-quote (side-by-side) state ----
  // Independent of the single-quote `quoteForm` above which is used by the
  // edit-quote dialog. Each item is one card in the multi-quote dialog.
  const [quoteForms, setQuoteForms] = useState([]);
  const [pricesIncludeGst, setPricesIncludeGst] = useState(true); // default ON

  const fetchData = useCallback(async () => {
    try {
      const [requestsRes, quotesRes, ordersRes, statsRes, profileRes, reviewsRes, hiddenRes] = await Promise.all([
        axios.get(`${API}/shop/available-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/shop/my-quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/shop/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/shop/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/shop/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/shop/reviews`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/shop/hidden-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setHiddenRequestIds(hiddenRes.data?.hidden_requests || []);
      
      // Handle available requests response
      const requestsData = requestsRes.data;
      if (requestsData.status === "pending_approval") {
        setAvailableRequests([]);
        setPendingApproval(true);
        setPendingMessage(requestsData.message);
        setShopSuspended(false);
      } else if (requestsData.status === "suspended") {
        setAvailableRequests([]);
        setPendingApproval(false);
        setShopSuspended(true);
        setSuspensionInfo({
          reason: requestsData.suspension_reason || null,
          pauseRequested: requestsData.pause_requested || false
        });
      } else if (requestsData.status === "approved" && Array.isArray(requestsData.requests)) {
        // Shop is approved - show available requests
        setAvailableRequests(requestsData.requests);
        setPendingApproval(false);
        setShopSuspended(false);
      } else if (Array.isArray(requestsData)) {
        // Legacy format - direct array
        setAvailableRequests(requestsData);
        setPendingApproval(false);
        setShopSuspended(false);
      } else {
        setAvailableRequests([]);
        setPendingApproval(false);
        setShopSuspended(false);
      }
      
      setMyQuotes(Array.isArray(quotesRes.data) ? quotesRes.data : []);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setStats(statsRes.data);
      
      // Profile and reviews
      if (profileRes.data) {
        setShopProfile(profileRes.data);
        setProfileForm({
          shop_name: profileRes.data.shop_name || "",
          phone: profileRes.data.phone || "",
          service_radius_km: profileRes.data.service_radius_km || 50,
          address: profileRes.data.address || {}
        });
      }
      if (reviewsRes.data) {
        setShopReviews(reviewsRes.data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleHideRequest = async (requestId) => {
    const reason = window.prompt(
      "Why are you not interested in this request? (optional)\nThis helps us improve matching. We'll stop sending you reminders for this request."
    );
    if (reason === null) return; // user cancelled
    try {
      await axios.post(
        `${API}/shop/hide-request/${requestId}`,
        { reason: reason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiddenRequestIds(prev => [...prev, requestId]);
      toast.success("Request hidden — you won't receive further reminders");
    } catch (error) {
      toast.error("Failed to hide request");
    }
  };

  const handleUnhideRequest = async (requestId) => {
    try {
      await axios.post(`${API}/shop/unhide-request/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHiddenRequestIds(prev => prev.filter(id => id !== requestId));
      toast.success("Request restored");
    } catch (error) {
      toast.error("Failed to restore request");
    }
  };

  // ========== MULTI-QUOTE submit ==========
  // Each card is validated and POSTed independently. Successful cards drop
  // out; failed cards stay in the list with their error message attached.
  const buildTyreSize = () => {
    const t = selectedRequest?.tyre_specs || {};
    const v = selectedRequest?.vehicle_info?.tyre_specs || {};
    const w = t.width || v.width;
    const p = t.aspect_ratio || t.profile || v.aspect_ratio;
    const r = t.rim_size || v.rim_size;
    return w ? `${w}/${p || ""}R${r || ""}` : null;
  };

  const validateQuoteSlot = (q, isTyreService) => {
    const total =
      (parseFloat(q.tyre_fitment_cost) || 0) +
      (parseFloat(q.alignment_cost) || 0) +
      (parseFloat(q.rotation_cost) || 0);
    if (total <= 0) return "Enter at least one price";
    if (isTyreService && (!q.tyre_brand?.trim() || !q.tyre_model?.trim()))
      return "Tyre brand and model are required";
    if (isTyreService && !q.speed_rating?.trim())
      return "Speed rating is required";
    if (!q.service_date_estimate) return "Earliest service date is required";
    if (!q.notes?.trim()) return "Notes to customer are required";
    return null;
  };

  // A slot is "pristine" when the shop hasn't touched any of its fields.
  // We seed 3 blank slots by default for first-time submits, so any unused
  // pristine slots should be silently ignored at submit time rather than
  // surfaced as validation errors.
  const isPristineSlot = (q) =>
    !q.tyre_brand?.trim() &&
    !q.tyre_model?.trim() &&
    !q.speed_rating?.trim() &&
    !q.tyre_fitment_cost &&
    !q.alignment_cost &&
    !q.rotation_cost &&
    !q.service_date_estimate &&
    !q.notes?.trim();

  const buildQuotePayload = (q, isTyreService) => {
    const tyreFitment = parseFloat(q.tyre_fitment_cost) || 0;
    const alignment = parseFloat(q.alignment_cost) || 0;
    const rotation = parseFloat(q.rotation_cost) || 0;
    const enteredTotal = tyreFitment + alignment + rotation;
    let subtotalExclGst, gst, totalInclGst;
    if (pricesIncludeGst) {
      totalInclGst = enteredTotal;
      subtotalExclGst = totalInclGst / 1.15;
      gst = totalInclGst - subtotalExclGst;
    } else {
      subtotalExclGst = enteredTotal;
      gst = subtotalExclGst * 0.15;
      totalInclGst = subtotalExclGst + gst;
    }
    return {
      request_id: selectedRequest.request_id,
      tyre_brand: isTyreService ? q.tyre_brand?.trim() || null : null,
      tyre_model: isTyreService ? q.tyre_model?.trim() || null : null,
      tyre_size: isTyreService ? buildTyreSize() : null,
      speed_rating: isTyreService ? q.speed_rating?.trim() || null : null,
      stock_availability: isTyreService ? q.stock_availability || "in_stock" : null,
      total_price: totalInclGst,
      price_breakdown: {
        tyre_fitment: tyreFitment,
        alignment,
        rotation,
        prices_include_gst: pricesIncludeGst,
        subtotal_excl_gst: subtotalExclGst,
        gst,
        total_incl_gst: totalInclGst,
      },
      fitment_estimate: formatDateForApi(q.service_date_estimate),
      quote_expiry: q.quote_expiry
        ? formatDateForApi(q.quote_expiry)
        : formatDateForApi(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
      notes: q.notes?.trim() || null,
    };
  };

  const handleSubmitAllQuotes = async () => {
    if (!selectedRequest || !quoteForms.length) return;
    const isTyreService =
      selectedRequest.service_type?.includes("tyres") ||
      selectedRequest.selected_services?.includes("tyres");

    setSubmittingQuote(true);

    // Validate each card; mark failures in-place. Pristine slots (untouched
    // default extras) are silently skipped, not flagged as errors.
    const validatedForms = quoteForms.map((q) => {
      if (q._submitted) return q;
      if (isPristineSlot(q)) return { ...q, _error: null, _skipped: true, _submitting: false };
      const err = validateQuoteSlot(q, isTyreService);
      return { ...q, _error: err, _skipped: false, _submitting: !err };
    });
    setQuoteForms(validatedForms);

    // Submit only valid, not-yet-submitted, non-skipped ones
    const indices = [];
    const submissions = [];
    validatedForms.forEach((q, idx) => {
      if (q._submitted || q._error || q._skipped) return;
      indices.push(idx);
      submissions.push(
        axios.post(`${API}/shop/submit-quote`, buildQuotePayload(q, isTyreService), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
    });

    if (!submissions.length) {
      setSubmittingQuote(false);
      const firstErr = validatedForms.find((q) => q._error)?._error;
      toast.error(firstErr || "Please fix the highlighted quotes first");
      return;
    }

    const results = await Promise.allSettled(submissions);
    let successCount = 0;
    const failed = [];
    let allSubmittedAfter = false;
    setQuoteForms((current) => {
      const next = current.map((q, idx) => {
        const resultIdx = indices.indexOf(idx);
        if (resultIdx === -1) return q;
        const result = results[resultIdx];
        if (result.status === "fulfilled") {
          successCount++;
          return { ...q, _submitted: true, _submitting: false, _error: null };
        }
        const detail =
          result.reason?.response?.data?.detail || "Failed to submit this quote";
        failed.push({ idx, detail });
        return { ...q, _submitted: false, _submitting: false, _error: detail };
      });
      // Compute cumulative success across both runs (previously-_submitted + this run).
      // Skipped (pristine) slots count as "done" for closing the dialog.
      allSubmittedAfter = next.length > 0 && next.every((q) => q._submitted || q._skipped);
      return next;
    });
    setSubmittingQuote(false);

    if (successCount > 0) {
      toast.success(
        `${successCount} ${successCount === 1 ? "quote" : "quotes"} submitted!`,
      );
    }
    if (failed.length > 0) {
      toast.error(
        `${failed.length} ${failed.length === 1 ? "quote" : "quotes"} failed — see highlighted card${failed.length === 1 ? "" : "s"}`,
      );
    }
    // If everything submitted (current + previous runs combined), close after a beat
    if (failed.length === 0 && allSubmittedAfter) {
      clearDraft(selectedRequest.request_id);
      setTimeout(() => {
        setQuoteDialogOpen(false);
        resetMultiQuoteForms();
        fetchData();
        // Mockup B: jump to My Quotes so the shop sees what the customer received
        setActiveTab("quotes");
      }, 800);
    } else {
      // Refresh dashboard data so quote counts on cards stay accurate
      fetchData();
    }
  };

  const resetMultiQuoteForms = () => {
    setQuoteForms([]);
    setPricesIncludeGst(true);
  };

  // Pre-flight validation for the Review step. Marks errors in-place and
  // returns true only if every non-pristine slot is valid AND at least one
  // is ready to send. PRISTINE slots (untouched default extras) are silently
  // ignored, not flagged as errors.
  const handlePreValidateQuotes = () => {
    if (!selectedRequest || !quoteForms.length) return false;
    const isTyreService =
      selectedRequest.service_type?.includes("tyres") ||
      selectedRequest.selected_services?.includes("tyres");
    const validated = quoteForms.map((q) => {
      if (q._submitted) return q;
      if (isPristineSlot(q)) return { ...q, _error: null, _skipped: true };
      const err = validateQuoteSlot(q, isTyreService);
      return { ...q, _error: err, _skipped: false };
    });
    setQuoteForms(validated);
    const errored = validated.filter((q) => !q._submitted && !q._skipped && q._error);
    if (errored.length > 0) {
      toast.error("Please fix the highlighted quotes first");
      return false;
    }
    const ready = validated.filter((q) => !q._submitted && !q._skipped && !q._error);
    if (!ready.length) {
      toast.error("Fill in at least one quote before submitting");
      return false;
    }
    return true;
  };

  // ========== DRAFT (localStorage, scoped per request) ==========
  // Lets shops fill out a multi-quote form, close the dialog, and resume
  // later without losing their work. Cleared automatically when all
  // quotes successfully submit.
  const draftKey = (rid) => `tyrematch_quote_draft_${rid}`;

  const loadDraftOrSeedQuoteForms = (rid) => {
    try {
      const raw = localStorage.getItem(draftKey(rid));
      if (raw) {
        const parsed = JSON.parse(raw);
        const restored = (parsed.forms || []).map((f) => ({
          ...blankQuoteSlot(),
          ...f,
          // Re-hydrate dates that JSON serialised as strings
          service_date_estimate: f.service_date_estimate
            ? new Date(f.service_date_estimate)
            : null,
          quote_expiry: f.quote_expiry ? new Date(f.quote_expiry) : null,
          // Drop any stale UI-only flags
          _error: null,
          _submitting: false,
          _submitted: false,
          _skipped: false,
        }));
        setQuoteForms(restored.length ? restored : [blankQuoteSlot()]);
        setPricesIncludeGst(parsed.pricesIncludeGst ?? true);
        toast.success("Draft loaded");
        return;
      }
    } catch {
      // Corrupt draft — fall through to fresh slots
    }
    setPricesIncludeGst(true);
    // Seed 3 blank slots by default (or fewer if the shop's remaining cap is
    // smaller). Pristine extras are silently ignored at submit time.
    const req = availableRequests.find((r) => r.request_id === rid);
    const isTyreService =
      req?.service_type?.includes("tyres") ||
      req?.selected_services?.includes("tyres");
    const maxQuotes = isTyreService ? 5 : 1;
    const remaining = Math.max(1, maxQuotes - (req?.shop_quotes_submitted || 0));
    const seedCount = Math.min(3, remaining);
    setQuoteForms(Array.from({ length: seedCount }, () => blankQuoteSlot()));
  };

  const handleSaveDraft = () => {
    if (!selectedRequest) return;
    const formsToSave = quoteForms
      .filter((q) => !q._submitted)
      .map((q) => ({
        ...q,
        _error: null,
        _submitting: false,
        _submitted: false,
      }));
    if (!formsToSave.length) {
      toast.info("Nothing to save — all quotes already submitted");
      return;
    }
    try {
      localStorage.setItem(
        draftKey(selectedRequest.request_id),
        JSON.stringify({ forms: formsToSave, pricesIncludeGst }),
      );
      toast.success("Draft saved on this device");
    } catch {
      toast.error("Couldn't save draft (browser storage full?)");
    }
  };

  const clearDraft = (rid) => {
    try {
      localStorage.removeItem(draftKey(rid));
    } catch {
      /* ignore */
    }
  };

  const resetQuoteForm = () => {
    setQuoteForm({
      tyre_brand: "",
      tyre_model: "",
      speed_rating: "",
      tyre_fitment_cost: "",
      alignment_cost: "",
      rotation_cost: "",
      prices_include_gst: false,
      service_date_estimate: null,
      quote_expiry: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      notes: "",
      edit_width: "",
      edit_profile: "",
      edit_rim_size: ""
    });
  };

  // Check if quote is within 10-minute edit window
  const isQuoteEditable = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMinutes = (now - created) / (1000 * 60);
    return diffMinutes <= 10;
  };

  const getQuoteEditTimeRemaining = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMinutes = (now - created) / (1000 * 60);
    const remaining = Math.max(0, 10 - diffMinutes);
    return remaining > 0 ? Math.ceil(remaining) : null;
  };

  const isQuoteDeletable = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMinutes = (now - created) / (1000 * 60);
    return diffMinutes <= 10; // Same as edit window
  };

  const [deletingQuote, setDeletingQuote] = useState(false);
  const [deletionRequestDialog, setDeletionRequestDialog] = useState({ open: false, quoteId: null });
  const [deletionReason, setDeletionReason] = useState("");
  const [submittingDeletion, setSubmittingDeletion] = useState(false);

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Are you sure you want to delete this quote? This action cannot be undone.")) {
      return;
    }
    
    setDeletingQuote(true);
    try {
      await axios.delete(`${API}/shop/quote/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote deleted successfully");
      setViewQuoteDialogOpen(false);
      setViewingQuote(null);
      resetQuoteForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    } finally {
      setDeletingQuote(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!deletionReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }
    setSubmittingDeletion(true);
    try {
      await axios.post(`${API}/shop/quote/${deletionRequestDialog.quoteId}/request-deletion`, 
        { reason: deletionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Deletion request submitted for admin review");
      setDeletionRequestDialog({ open: false, quoteId: null });
      setDeletionReason("");
      setViewQuoteDialogOpen(false);
      setViewingQuote(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit deletion request");
    } finally {
      setSubmittingDeletion(false);
    }
  };

  const handleViewQuote = (quote) => {
    setViewingQuote(quote);
    // Map price breakdown to new format
    const priceBreakdown = quote.price_breakdown || {};
    setQuoteForm({
      tyre_brand: quote.tyre_brand || "",
      tyre_model: quote.tyre_model || "",
      tyre_size: quote.tyre_size || "",
      tyre_fitment_cost: (priceBreakdown.tyre_fitment || priceBreakdown.tyres || "").toString(),
      alignment_cost: (priceBreakdown.alignment || "").toString(),
      rotation_cost: (priceBreakdown.rotation || "").toString(),
      prices_include_gst: priceBreakdown.prices_include_gst || false,
      service_date_estimate: (quote.fitment_estimate || quote.service_date_estimate) ? new Date(quote.fitment_estimate || quote.service_date_estimate) : null,
      quote_expiry: quote.quote_expiry ? new Date(quote.quote_expiry) : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      notes: quote.notes || "",
      stock_availability: quote.stock_availability || "in_stock"
    });
    setEditQuoteMode(false);
    setViewQuoteDialogOpen(true);
  };

  const handleSaveQuoteEdit = async () => {
    if (!viewingQuote) return;
    
    // Calculate totals based on service type (same logic as submit)
    const tyreFitment = parseFloat(quoteForm.tyre_fitment_cost) || 0;
    const alignment = parseFloat(quoteForm.alignment_cost) || 0;
    const rotation = parseFloat(quoteForm.rotation_cost) || 0;
    const enteredTotal = tyreFitment + alignment + rotation;
    
    if (enteredTotal <= 0) {
      toast.error("Please enter at least one cost");
      return;
    }

    // Service date is mandatory
    if (!quoteForm.service_date_estimate) {
      toast.error("Please select an earliest service date");
      return;
    }
    
    // Calculate GST based on toggle
    let subtotalExclGst, gst, totalInclGst;
    if (quoteForm.prices_include_gst) {
      totalInclGst = enteredTotal;
      subtotalExclGst = totalInclGst / 1.15;
      gst = totalInclGst - subtotalExclGst;
    } else {
      subtotalExclGst = enteredTotal;
      gst = subtotalExclGst * 0.15;
      totalInclGst = subtotalExclGst + gst;
    }
    
    // Build price breakdown
    const priceBreakdown = {
      tyre_fitment: tyreFitment,
      alignment: alignment,
      rotation: rotation,
      prices_include_gst: quoteForm.prices_include_gst,
      subtotal_excl_gst: subtotalExclGst,
      gst: gst,
      total_incl_gst: totalInclGst
    };

    setSavingQuoteEdit(true);
    try {
      await axios.put(
        `${API}/shop/quote/${viewingQuote.quote_id}`,
        {
          tyre_brand: quoteForm.tyre_brand || null,
          tyre_model: quoteForm.tyre_model || null,
          tyre_size: quoteForm.tyre_size || null,
          speed_rating: quoteForm.speed_rating || null,
          total_price: totalInclGst,
          price_breakdown: priceBreakdown,
          fitment_estimate: formatDateForApi(quoteForm.service_date_estimate),
          quote_expiry: quoteForm.quote_expiry ? formatDateForApi(quoteForm.quote_expiry) : formatDateForApi(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
          notes: quoteForm.notes || null,
          stock_availability: quoteForm.stock_availability
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Quote updated successfully");
      setEditQuoteMode(false);
      setViewQuoteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update quote");
    } finally {
      setSavingQuoteEdit(false);
    }
  };

  const handleOpenConfirmAvailability = (order) => {
    setSelectedOrder(order);
    setAvailabilityForm({
      stock_confirmed: true,
      proposed_service_date: null,
      proposed_service_time: "",
      notes: ""
    });
    setConfirmAvailabilityDialogOpen(true);
  };

  const handleConfirmAvailability = async () => {
    if (!selectedOrder) return;
    
    if (availabilityForm.stock_confirmed && !availabilityForm.proposed_service_date) {
      toast.error("Please select a proposed fitment date");
      return;
    }

    setSubmittingAvailability(true);
    try {
      await axios.post(
        `${API}/shop/confirm-availability`,
        {
          order_id: selectedOrder.order_id,
          stock_confirmed: availabilityForm.stock_confirmed,
          proposed_service_date: availabilityForm.proposed_service_date 
            ? format(availabilityForm.proposed_service_date, "yyyy-MM-dd") 
            : null,
          proposed_service_time: availabilityForm.proposed_service_time || null,
          notes: availabilityForm.notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        availabilityForm.stock_confirmed 
          ? "Availability confirmed! Customer will be notified to confirm the fitment date." 
          : "Order cancelled. Customer has been notified."
      );
      setConfirmAvailabilityDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm availability");
    } finally {
      setSubmittingAvailability(false);
    }
  };

  const handleMarkComplete = async (orderId) => {
    setMarkingComplete(true);
    try {
      await axios.post(
        `${API}/shop/mark-complete/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Job marked as complete! Customer has been notified.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as complete");
    } finally {
      setMarkingComplete(false);
    }
  };

  // Step 1: Confirm Job - reveals shop contact to customer
  const handleConfirmJob = async () => {
    if (!selectedOrderForAction) return;
    setConfirmingJob(true);
    try {
      await axios.post(
        `${API}/shop/orders/${selectedOrderForAction.order_id}/confirm-job`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Job confirmed! Customer can now see your contact details.");
      setConfirmJobDialogOpen(false);
      setSelectedOrderForAction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm job");
    } finally {
      setConfirmingJob(false);
    }
  };

  // Step 2: Assign Service Date
  const handleAssignServiceDate = async () => {
    if (!selectedOrderForAction) return;
    if (!serviceDateForm.service_date) {
      toast.error("Please select a service date");
      return;
    }
    setAssigningDate(true);
    try {
      await axios.post(
        `${API}/shop/orders/${selectedOrderForAction.order_id}/assign-date`,
        {
          service_date: serviceDateForm.service_date,
          service_time: serviceDateForm.service_time || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Service date assigned successfully!");
      setAssignDateDialogOpen(false);
      setSelectedOrderForAction(null);
      setServiceDateForm({ service_date: "", service_time: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign date");
    } finally {
      setAssigningDate(false);
    }
  };

  // Step 3: Mark Complete with details popup
  const handleCompleteWithDetails = async () => {
    if (!selectedOrderForAction) return;
    if (!completionForm.service_date || !completionForm.payment_method) {
      toast.error("Please fill in service date and payment method");
      return;
    }
    setMarkingComplete(true);
    try {
      await axios.post(
        `${API}/shop/orders/${selectedOrderForAction.order_id}/complete-with-details`,
        completionForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Job completed! Customer has been sent the service confirmation and review request.");
      setCompletionDialogOpen(false);
      setSelectedOrderForAction(null);
      setCompletionForm({ service_date: "", service_time: "", payment_method: "", completion_notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete job");
    } finally {
      setMarkingComplete(false);
    }
  };

  // Open completion dialog instead of direct mark complete
  const openCompletionDialog = (order) => {
    setSelectedOrderForAction(order);
    setCompletionForm({
      service_date: order.assigned_service_date || new Date().toISOString().split('T')[0],
      service_time: order.assigned_service_time || "",
      payment_method: "",
      completion_notes: ""
    });
    setCompletionDialogOpen(true);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleRequestPause = async () => {
    if (!pauseReason || pauseReason.trim().length < 10) {
      toast.error("Please provide a reason (minimum 10 characters)");
      return;
    }
    
    setSubmittingPause(true);
    try {
      await axios.post(
        `${API}/shop/request-pause`,
        { reason: pauseReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your account has been paused. Contact support when ready to resume.");
      setPauseDialogOpen(false);
      setPauseReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pause account");
    } finally {
      setSubmittingPause(false);
    }
  };

  const handleRequestResume = async () => {
    try {
      await axios.post(
        `${API}/shop/request-resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your account has been resumed! You will now receive new quote requests.");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume account");
    }
  };

  const formatTimeRemaining = (timeRemaining) => {
    if (timeRemaining.expired) return "Expired";
    const { days, hours, minutes } = timeRemaining;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shop-dashboard">
      <SEO page="shopDashboard" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/">
                <img src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} alt="TyreMatch" className="h-10" />
              </Link>
              <FranchiseReturnLink />
            </div>

            <div className="flex items-center gap-2">
              <ImpersonationBanner />
              {/* Settings/Account Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/shop/account")}
                className="flex items-center gap-2 rounded-full px-3"
                data-testid="settings-btn"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline text-sm">{user?.shop_name}</span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="rounded-full text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pending Approval Banner */}
        {pendingApproval && (
          <Card className="mb-6 border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-yellow-600 mb-1">Account Pending Approval</h3>
                  <p className="text-muted-foreground mb-3">
                    {pendingMessage || "Your shop registration is being reviewed by our team. You will be notified via email once approved."}
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                      Status: Pending Review
                    </Badge>
                    <span className="text-muted-foreground">
                      Typical review time: 1-2 business days
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Suspended Account Banner */}
        {shopSuspended && (
          <Card className="mb-6 border-red-500/30 bg-red-500/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-red-600 mb-1">Account Suspended</h3>
                  <p className="text-muted-foreground mb-3">
                    {suspensionInfo?.pauseRequested 
                      ? "You have paused your account. You will not receive new quote requests while paused."
                      : "Your account has been suspended. You will not receive new quote requests."
                    }
                  </p>
                  {suspensionInfo?.reason && (
                    <p className="text-sm text-muted-foreground mb-3">
                      <strong>Reason:</strong> {suspensionInfo.reason}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
                      {suspensionInfo?.pauseRequested ? "Status: Paused by You" : "Status: Suspended"}
                    </Badge>
                    {suspensionInfo?.pauseRequested && (
                      <Button size="sm" variant="default" onClick={handleRequestResume} data-testid="resume-account-btn">
                        Resume Account
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-1">
            {user?.shop_name}
          </h1>
          <p className="text-muted-foreground">
            View and respond to quote requests in your area
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{stats?.total_quotes || 0}</p>
                  <p className="text-xs text-muted-foreground">Quotes Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Package className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{stats?.total_orders || 0}</p>
                  <p className="text-xs text-muted-foreground">Orders Won</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">${stats?.total_revenue?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{stats?.conversion_rate?.toFixed(1) || 0}%</p>
                  <p className="text-xs text-muted-foreground">Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="available" data-testid="available-tab" className="relative">
              Available Requests ({availableRequests.filter(r => !hiddenRequestIds.includes(r.request_id)).length})
              {availableRequests.some(r => !hiddenRequestIds.includes(r.request_id) && (r.shop_quotes_submitted || 0) === 0) && (
                <span className="ml-2 w-2 h-2 rounded-full bg-primary animate-pulse" data-testid="available-tab-new-activity-dot" />
              )}
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="quotes-tab">
              My Quotes ({myQuotes.length})
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab" className="relative">
              Orders ({orders.length})
              {orders.filter(o => o.order_status === "confirmed" || o.order_status === "pending_fitment" || o.order_status === "awaiting_shop_confirmation").length > 0 && (
                <span className="ml-2 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-orange-500 text-white text-xs font-medium px-1 animate-pulse">
                  {orders.filter(o => o.order_status === "confirmed" || o.order_status === "pending_fitment" || o.order_status === "awaiting_shop_confirmation").length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Available Requests Tab */}
          <TabsContent value="available" className="space-y-4">
            {/* Active / Hidden sub-tabs */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={requestTab === "active" ? "default" : "outline"}
                size="sm"
                onClick={() => setRequestTab("active")}
                data-testid="request-tab-active"
              >
                Active ({availableRequests.filter(r => !hiddenRequestIds.includes(r.request_id)).length})
              </Button>
              <Button
                variant={requestTab === "hidden" ? "default" : "outline"}
                size="sm"
                onClick={() => setRequestTab("hidden")}
                data-testid="request-tab-hidden"
              >
                Hidden ({availableRequests.filter(r => hiddenRequestIds.includes(r.request_id)).length})
              </Button>
            </div>

            {availableRequests.filter(r => requestTab === "active" ? !hiddenRequestIds.includes(r.request_id) : hiddenRequestIds.includes(r.request_id)).length === 0 ? (
              <Card className="text-center p-12">
                <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">
                  {requestTab === "active" ? "No requests available" : "No hidden requests"}
                </h3>
                <p className="text-muted-foreground">
                  {requestTab === "active" ? "New requests in your area will appear here" : "Requests you mark as 'Not Interested' will appear here"}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {availableRequests.filter(r => requestTab === "active" ? !hiddenRequestIds.includes(r.request_id) : hiddenRequestIds.includes(r.request_id)).map((request) => (
                  <Card key={request.request_id} className="card-hover" data-testid={`request-${request.request_id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-heading text-xl font-semibold">
                              {request.request_number || `#${request.request_id}`}
                            </h3>
                            {request.request_type === "urgent" && (
                              <Badge className="bg-primary/10 text-primary border-primary/30">
                                <Zap className="h-3 w-3 mr-1" />
                                Urgent
                              </Badge>
                            )}
                            {request.shop_quotes_submitted > 0 && (
                              <Badge className={request.shop_quotes_submitted >= (request.max_quotes || 5) 
                                ? "bg-gray-500/10 text-gray-600 border-gray-500/30" 
                                : "bg-blue-500/10 text-blue-600 border-blue-500/30"}>
                                {request.shop_quotes_submitted}/{request.max_quotes || 5} quotes sent
                              </Badge>
                            )}
                            {request.comparison_intent === "specific" && (
                              <Badge
                                className="bg-blue-500/10 text-blue-600 border-blue-500/30"
                                title="Customer wants this exact brand/model"
                                data-testid={`shop-req-intent-${request.request_id}`}
                              >
                                Exact match
                              </Badge>
                            )}
                            {request.comparison_intent === "budget" && (
                              <Badge
                                className="bg-purple-500/10 text-purple-600 border-purple-500/30"
                                title={`Budget tier${request.driving_priority ? ` · priority: ${request.driving_priority.replace(/_/g, " ")}` : ""}`}
                                data-testid={`shop-req-intent-${request.request_id}`}
                              >
                                Budget match{request.driving_priority ? ` · ${request.driving_priority.replace(/_/g, " ")}` : ""}
                              </Badge>
                            )}
                            {request.comparison_intent === "open" && (
                              <Badge
                                className="bg-amber-500/10 text-amber-600 border-amber-500/30"
                                title="Customer is open to suggestions"
                                data-testid={`shop-req-intent-${request.request_id}`}
                              >
                                Open to suggestions
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Package className="h-4 w-4" />
                              {getServiceLabel(request)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {request.distance_km?.toFixed(1)} km away
                            </span>
                            {(request.vehicle_info?.rego || request.rego) && (
                              <span className="flex items-center gap-1">
                                <FileText className="h-4 w-4" />
                                Rego: {request.vehicle_info?.rego || request.rego}
                              </span>
                            )}
                            {(request.vehicle_info?.make || request.vehicle_info?.model) && (
                              <span className="flex items-center gap-1 text-foreground font-medium" data-testid={`shop-req-vehicle-${request.request_id}`}>
                                {[
                                  request.vehicle_info?.year,
                                  request.vehicle_info?.make,
                                  request.vehicle_info?.model,
                                ].filter(Boolean).join(" ")}
                                {request.vehicle_info?.sub_model && (
                                  <span className="text-muted-foreground"> · {request.vehicle_info.sub_model}</span>
                                )}
                              </span>
                            )}
                            {/* Only show tyre count for services that include tyres */}
                            {request.quantity && (request.service_type?.includes("tyres") || request.selected_services?.includes("tyres")) && (
                              <span>{request.quantity} tyres</span>
                            )}
                          </div>

                          {request.tyre_specs && request.tyre_specs.width && (
                            <p className="text-sm">
                              Tyre size: {request.tyre_specs.width}/{request.tyre_specs.aspect_ratio || request.tyre_specs.profile || "—"}R{request.tyre_specs.rim_size || "—"}
                            </p>
                          )}

                          {request.brand_preference && (
                            <p className="text-sm text-muted-foreground">
                              Preferred: {request.brand_preference}
                            </p>
                          )}

                          {request.price_range && (
                            <p className="text-sm text-muted-foreground">
                              Price Range: {
                                { budget: "Budget", lower: "Budget", mid: "Mid-Range", premium: "Premium", any: "Any Price Range", winter: "Winter Tyres", all_season: "All Season", ev: "EV Tyres" }[request.price_range] || request.price_range
                              }
                            </p>
                          )}

                          {request.customer_note && (
                            <div className="mt-2 p-2 bg-blue-500/5 border border-blue-500/20 rounded-md">
                              <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-0.5">Customer Note</p>
                              <p className="text-sm">{request.customer_note}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className={request.time_remaining?.expired ? "text-destructive" : ""}>
                                {formatTimeRemaining(request.time_remaining)}
                              </span>
                            </div>
                          </div>

                          <Button
                            onClick={() => {
                              setSelectedRequest(request);
                              loadDraftOrSeedQuoteForms(request.request_id);
                              setQuoteDialogOpen(true);
                            }}
                            disabled={!request.can_submit_quote || request.time_remaining?.expired}
                            className="bg-primary text-primary-foreground"
                            data-testid={`submit-quote-${request.request_id}`}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Submit Quote
                          </Button>
                          {requestTab === "active" ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleHideRequest(request.request_id)}
                              disabled={(request.shop_quotes_submitted || 0) > 0}
                              title={
                                (request.shop_quotes_submitted || 0) > 0
                                  ? "You've already submitted a quote for this request"
                                  : undefined
                              }
                              data-testid={`hide-request-${request.request_id}`}
                            >
                              <EyeOff className="h-4 w-4 mr-2" />
                              Not Interested
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnhideRequest(request.request_id)}
                              data-testid={`unhide-request-${request.request_id}`}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Quotes Tab */}
          <TabsContent value="quotes" className="space-y-4">
            {myQuotes.length === 0 ? (
              <Card className="text-center p-12">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">No quotes submitted</h3>
                <p className="text-muted-foreground">
                  Your submitted quotes will appear here
                </p>
              </Card>
            ) : (
              <ShopMyQuotesGroupedList
                quotes={myQuotes}
                onView={handleViewQuote}
                isQuoteEditable={isQuoteEditable}
              />
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            {orders.length === 0 ? (
              <Card className="text-center p-12">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">No orders yet</h3>
                <p className="text-muted-foreground">
                  Won orders will appear here
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Card 
                    key={order.order_id} 
                    className={cn(
                      "card-hover",
                      order.order_status === "awaiting_shop_availability_confirmation" && "border-yellow-500/50 bg-yellow-500/5"
                    )}
                    data-testid={`order-${order.order_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-heading text-xl font-semibold">
                              Order #{order.order_id.substring(0, 8)}
                            </h3>
                            <Badge className={
                              order.order_status === "confirmed" 
                                ? "bg-green-500/10 text-green-600 border-green-500/30"
                                : order.order_status === "completed"
                                ? "bg-blue-500/10 text-blue-600 border-blue-500/30"
                                : STATUS_COLORS[order.order_status] || "bg-gray-500/10 text-gray-600"
                            }>
                              {order.order_status === "awaiting_shop_availability_confirmation" 
                                ? "Action Required" 
                                : order.order_status === "confirmed"
                                ? "Confirmed"
                                : order.order_status === "pending_service"
                                ? "Confirmed"
                                : order.order_status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                            </Badge>
                          </div>

                          {/* Customer Contact Info - for confirmed orders */}
                          {(order.order_status === "confirmed" || order.order_status === "pending_service") && (
                            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <p className="text-sm font-medium text-blue-800 dark:text-blue-200 flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Customer will call to book appointment
                              </p>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                Customer has been sent your contact details via email
                              </p>
                            </div>
                          )}

                          {order.order_status === "awaiting_shop_availability_confirmation" && (
                            <div className="flex items-center gap-2 text-yellow-600">
                              <AlertCircle className="h-4 w-4" />
                              <p className="text-sm font-medium">
                                Please confirm stock availability and propose a fitment date
                              </p>
                            </div>
                          )}

                          {order.proposed_service_date && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CalendarIcon className="h-4 w-4" />
                              <span>Proposed: {formatNZDate(order.proposed_service_date)}</span>
                              {order.proposed_service_time && <span>at {order.proposed_service_time}</span>}
                            </div>
                          )}

                          <p className="text-sm text-muted-foreground">
                            Created: {formatNZDate(order.created_at)}
                          </p>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right space-y-1">
                            <p className="text-2xl font-heading font-bold text-primary">
                              ${(order.shop_payout || order.order_amount || 0).toFixed(2)}
                            </p>
                            {order.commission_rate > 0 && (
                              <p className="text-xs text-muted-foreground">
                                Your payout (after {(order.commission_rate * 100).toFixed(0)}% commission)
                              </p>
                            )}
                          </div>

                          {/* New Workflow Buttons */}
                          <div className="flex flex-col gap-2">
                            {/* Mark Complete - for confirmed orders */}
                            {(order.order_status === "confirmed" || order.order_status === "pending_service") && (
                              <Button
                                onClick={() => openCompletionDialog(order)}
                                disabled={markingComplete}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`mark-complete-${order.order_id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {markingComplete ? "Marking..." : "Mark Complete"}
                              </Button>
                            )}

                            {/* Legacy support for old pending_fitment status */}
                            {order.order_status === "pending_fitment" && (
                              <Button
                                onClick={() => openCompletionDialog(order)}
                                disabled={markingComplete}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`mark-complete-${order.order_id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {markingComplete ? "Marking..." : "Mark Complete"}
                              </Button>
                            )}

                            {/* View Confirmation for completed orders */}
                            {order.order_status === "completed" && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setReceiptOrderId(order.order_id);
                                  setReceiptDialogOpen(true);
                                }}
                                data-testid={`view-receipt-shop-${order.order_id}`}
                              >
                                <Receipt className="h-4 w-4 mr-2" />
                                View Confirmation
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Customer Details Card - shown for all accepted orders (no payment wall) */}
                        {order.customer && (
                          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2 mb-3">
                              <User className="h-4 w-4" />
                              Customer Details
                            </h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Name</p>
                                <p className="font-medium">{order.customer.name}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Email</p>
                                <p className="font-medium">{order.customer.email}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Phone</p>
                                <p className="font-medium">{order.customer.phone || "N/A"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Address</p>
                                <p className="font-medium">
                                  {order.customer.address?.full_address || "N/A"}
                                </p>
                              </div>
                            </div>
                            {order.fitment_date && (
                              <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                                <p className="text-muted-foreground text-sm">Service Date</p>
                                <p className="font-medium">
                                  {formatNZDate(order.fitment_date)} {order.fitment_time || ""}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>


      {/* Quote Submission Dialog (multi-quote) */}
      <ShopQuoteFormDialog
        quoteDialogOpen={quoteDialogOpen}
        setQuoteDialogOpen={setQuoteDialogOpen}
        selectedRequest={selectedRequest}
        quoteForms={quoteForms}
        setQuoteForms={setQuoteForms}
        pricesIncludeGst={pricesIncludeGst}
        setPricesIncludeGst={setPricesIncludeGst}
        submittingQuote={submittingQuote}
        handleSubmitAllQuotes={handleSubmitAllQuotes}
        handlePreValidateQuotes={handlePreValidateQuotes}
        handleSaveDraft={handleSaveDraft}
        resetMultiQuoteForms={resetMultiQuoteForms}
        getServiceLabel={getServiceLabel}
      />

      {/* View/Edit Quote Dialog */}
      <ShopQuoteViewDialog
        viewQuoteDialogOpen={viewQuoteDialogOpen}
        setViewQuoteDialogOpen={setViewQuoteDialogOpen}
        viewingQuote={viewingQuote}
        setViewingQuote={setViewingQuote}
        editQuoteMode={editQuoteMode}
        setEditQuoteMode={setEditQuoteMode}
        quoteForm={quoteForm}
        setQuoteForm={setQuoteForm}
        savingQuoteEdit={savingQuoteEdit}
        handleSaveQuoteEdit={handleSaveQuoteEdit}
        handleDeleteQuote={handleDeleteQuote}
        isQuoteEditable={isQuoteEditable}
        isQuoteDeletable={isQuoteDeletable}
        getQuoteEditTimeRemaining={getQuoteEditTimeRemaining}
        deletingQuote={deletingQuote}
        onRequestDeletion={(quoteId) => setDeletionRequestDialog({ open: true, quoteId })}
        resetQuoteForm={resetQuoteForm}
        formatDateForApi={formatDateForApi}
      />

      {/* Order Workflow Dialogs */}
      <ShopOrderDialogs
        confirmAvailabilityDialogOpen={confirmAvailabilityDialogOpen}
        setConfirmAvailabilityDialogOpen={setConfirmAvailabilityDialogOpen}
        confirmJobDialogOpen={confirmJobDialogOpen}
        setConfirmJobDialogOpen={setConfirmJobDialogOpen}
        assignDateDialogOpen={assignDateDialogOpen}
        setAssignDateDialogOpen={setAssignDateDialogOpen}
        completionDialogOpen={completionDialogOpen}
        setCompletionDialogOpen={setCompletionDialogOpen}
        pauseDialogOpen={pauseDialogOpen}
        setPauseDialogOpen={setPauseDialogOpen}
        selectedOrder={selectedOrder}
        setSelectedOrder={setSelectedOrder}
        selectedOrderForAction={selectedOrderForAction}
        availabilityForm={availabilityForm}
        setAvailabilityForm={setAvailabilityForm}
        serviceDateForm={serviceDateForm}
        setServiceDateForm={setServiceDateForm}
        completionForm={completionForm}
        setCompletionForm={setCompletionForm}
        pauseReason={pauseReason}
        setPauseReason={setPauseReason}
        submittingAvailability={submittingAvailability}
        submittingPause={submittingPause}
        confirmingJob={confirmingJob}
        assigningDate={assigningDate}
        markingComplete={markingComplete}
        handleConfirmAvailability={handleConfirmAvailability}
        handleConfirmJob={handleConfirmJob}
        handleAssignServiceDate={handleAssignServiceDate}
        handleCompleteWithDetails={handleCompleteWithDetails}
        handleRequestPause={handleRequestPause}
      />

      {/* Deletion Request Dialog */}
      <Dialog open={deletionRequestDialog.open} onOpenChange={(open) => { if (!open) { setDeletionRequestDialog({ open: false, quoteId: null }); setDeletionReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Request Quote Deletion</DialogTitle>
            <DialogDescription>
              This will submit a deletion request for admin review. Please provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Reason for deletion <span className="text-destructive">*</span></Label>
              <Textarea
                placeholder="Explain why this quote should be deleted..."
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                rows={3}
                data-testid="deletion-reason-input"
              />
              {!deletionReason.trim() && deletionReason !== "" && (
                <p className="text-xs text-destructive">A reason is required</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeletionRequestDialog({ open: false, quoteId: null }); setDeletionReason(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRequestDeletion} disabled={submittingDeletion || !deletionReason.trim()} data-testid="submit-deletion-request-btn">
              {submittingDeletion ? "Submitting..." : "Submit Deletion Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
